import { useNegotiateBrandCost } from "@entities/brief/lib/hooks/useNegotiateBrandCost";
import { ToastService } from "@shared/lib/services";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import {
  formatInrDisplayAmount,
  parseInrAmountInput,
  sanitizeInrAmountInput,
} from "@shared/lib/utils/inrAmountInWords";
import { Button, Input, InrAmountWordsLine, Popover } from "@shared/ui";
import { ReactNode, useEffect, useState } from "react";
import styles from "../negotiateInfluencerCommercials/styles.module.scss";

type NegotiateBrandCommercialsPopoverProps = {
  briefInfluencer: IBriefInfluencerV2;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
};

export function NegotiateBrandCommercialsPopover({
  briefInfluencer,
  open,
  onOpenChange,
  children,
}: NegotiateBrandCommercialsPopoverProps) {
  const handleClose = () => onOpenChange(false);

  const handleOpenChange = (visible: boolean) => {
    if (!visible) {
      onOpenChange(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      destroyTooltipOnHide
      overlayInnerStyle={{ padding: 12 }}
      content={
        open ? (
          <NegotiateBrandCommercialsPopoverContent
            briefInfluencer={briefInfluencer}
            onClose={handleClose}
          />
        ) : null
      }
    >
      <span>{children}</span>
    </Popover>
  );
}

type NegotiateBrandCommercialsPopoverContentProps = {
  briefInfluencer: IBriefInfluencerV2;
  onClose: () => void;
};

function NegotiateBrandCommercialsPopoverContent({
  briefInfluencer,
  onClose,
}: NegotiateBrandCommercialsPopoverContentProps) {
  const { negotiate, isNegotiating, isNegotiated } = useNegotiateBrandCost(
    briefInfluencer.briefId
  );

  const [value, setValue] = useState("");

  const influencerCommercials = formatInrDisplayAmount(
    briefInfluencer.influencerCost?.cost
  );

  useEffect(() => {
    if (isNegotiated) {
      ToastService.success("Negotiation requested");
      onClose();
    }
  }, [isNegotiated, onClose]);

  const handleSubmit = () => {
    const targetBrandCost = parseInrAmountInput(value);

    if (targetBrandCost == null) {
      ToastService.error("Please enter target brand commercial");
      return;
    }

    negotiate({
      briefInfluencerId: briefInfluencer._id,
      targetBrandCost,
      remarks: "",
    });
  };

  return (
    <div
      className={styles.popover}
      onClick={(event) => event.stopPropagation()}
    >
      <h4 className={styles.title}>Request brand negotiation</h4>
      <p className={styles.description}>
        Set a target brand commercial for pricing to work against on this
        creator.
      </p>

      {influencerCommercials ? (
        <div className={styles.pricingQuoteBlock}>
          <p className={styles.pricingQuoteLabel}>Influencer commercials</p>
          <div>
            <p className={styles.pricingQuoteAmount}>₹{influencerCommercials}</p>
            <InrAmountWordsLine
              value={influencerCommercials}
              className={styles.pricingQuoteWords}
            />
          </div>
        </div>
      ) : null}

      <label className={styles.fieldLabel} htmlFor="negotiate-target-brand-cost">
        Target brand commercial (INR)
      </label>
      <div className={styles.amountInputWrap}>
        <Input
          id="negotiate-target-brand-cost"
          inputMode="numeric"
          value={value}
          prefix="₹"
          onChange={(event) =>
            setValue(sanitizeInrAmountInput(event.target.value))
          }
          onPressEnter={handleSubmit}
          className={styles.amountInput}
          placeholder="e.g. 2,50,000"
          autoFocus
        />
      </div>
      <InrAmountWordsLine value={value} className={styles.mutedText} />

      <div className={styles.actions}>
        <Button type="text" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="small"
          loading={isNegotiating}
          disabled={!value.trim()}
          onClick={handleSubmit}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
